import type { ArticleCategory } from "@/lib/articles/categories";
import { type GuestAuthor, JAKUB_ANTALIK } from "@/lib/articles/guest-authors";
import { domain } from "@/lib/constants";

const month = (year: number, monthNumber: number) =>
  new Date(year, monthNumber - 1);

export interface ArticleMeta {
  author?: GuestAuthor;
  category: ArticleCategory;
  className?: string;
  date: Date;
  description: string;
  free?: boolean;
  hidden?: boolean;
  isNew?: boolean;
  issue: number;
  issueVariant?: "transparent";
  slug: string;
  src?: string;
  srcDark?: string;
  title: string;
}

const RAW_ARTICLES = [
  {
    category: "general",
    date: month(2026, 5),
    description: "Introduction to the magazine.",
    issue: 1,
    slug: "introduction",
    title: "Introduction",
  },
  {
    category: "color",
    date: month(2026, 5),
    description: "What are OKLCH colors?",
    issue: 2,
    slug: "what-are-oklch-colors",
    title: "What are OKLCH colors?",
  },
  {
    category: "color",
    date: month(2026, 5),
    description:
      "A deep dive into how gradients work, how color models affect them and why some gradients look better than others.",
    free: true,
    issue: 3,
    issueVariant: "transparent",
    slug: "understanding-gradients",
    title: "Understanding gradients",
  },
  {
    category: "animation",
    date: month(2026, 5),
    description: "How I use shared layout animations.",
    free: true,
    issue: 4,
    slug: "how-i-use-shared-layout-animations",
    title: "How I use shared layout animations",
  },
  {
    category: "animation",
    date: month(2026, 5),
    description:
      "An article about the will-change CSS property and how it works.",
    issue: 5,
    slug: "will-change-in-css",
    title: "will-change in CSS",
  },
  {
    category: "general",
    className: "bg-gray-300",
    date: month(2026, 5),
    description: "Using AI as a Design Engineer",
    issue: 6,
    slug: "using-ai-as-a-design-engineer",
    title: "Using AI as a design engineer",
  },
  {
    category: "animation",
    date: month(2026, 5),
    description: "Drag Gestures on the Web",
    issue: 7,
    slug: "drag-gestures-on-the-web",
    title: "Drag gestures on the web",
  },
  {
    category: "animation",
    date: month(2026, 5),
    description: "Using Gestures in Motion",
    issue: 8,
    slug: "using-gestures-in-motion",
    title: "Using gestures in motion",
  },
  {
    category: "general",
    date: month(2026, 5),
    description:
      "A collection of details that make your interfaces feel better.",
    free: true,
    issue: 9,
    slug: "details-that-make-interfaces-feel-better",
    title: "Details that make interfaces feel better",
  },
  {
    category: "general",
    date: month(2026, 5),
    description: "Sharing Your Work Online",
    issue: 10,
    slug: "sharing-your-work-online",
    src: "https://ho1jr3x2dcwdu3t5.public.blob.vercel-storage.com/issues-images/sharing-your-work-online.avif",
    srcDark:
      "https://ho1jr3x2dcwdu3t5.public.blob.vercel-storage.com/issues-images/sharing-your-work-online-dark.avif?v=2",
    title: "Sharing your work online",
  },
  {
    category: "animation",
    date: month(2026, 5),
    description: "Reviewing your animations as a design engineer.",
    issue: 11,
    slug: "review-your-animations",
    src: "https://ho1jr3x2dcwdu3t5.public.blob.vercel-storage.com/issues-images/watch-back-your-animations.avif?v=2",
    srcDark:
      "https://ho1jr3x2dcwdu3t5.public.blob.vercel-storage.com/issues-images/watch-back-your-animations-dark.avif?v=2",
    title: "Reviewing your animations",
  },
  {
    category: "animation",
    date: month(2026, 6),
    description:
      "An animated sign-in dialog built on top of the Base UI dialog primitive.",
    issue: 12,
    slug: "building-an-animated-sign-in-dialog",
    title: "Building an animated sign-in dialog",
  },
  {
    category: "general",
    date: month(2026, 6),
    description:
      "In the age of AI, knowing what not to build might be the most important skill of all.",
    issue: 13,
    slug: "less-is-more",
    title: "Less is more, more or less",
  },
  {
    category: "typography",
    date: month(2026, 7),
    description:
      "The first half of a practical tour of web typography: choosing a typeface, font formats and loading, variable fonts and OpenType features.",
    issue: 14,
    slug: "typography-manual-for-the-web",
    title: "Typography manual for the web",
  },
  {
    category: "typography",
    date: month(2026, 7),
    description:
      "The second half of the typography manual: spacing and rhythm, wrapping and overflow, underlines, forms and accessibility.",
    issue: 15,
    slug: "working-with-type",
    title: "Working with type",
  },
  {
    category: "general",
    date: month(2026, 8),
    description:
      "Why accessibility is the same craft as the rest of interface work.",
    issue: 16,
    slug: "accessibility-as-craft",
    title: "Accessibility as craft",
  },
  {
    author: JAKUB_ANTALIK,
    category: "guest",
    date: month(2026, 9),
    description:
      "A guest issue on how duration, easing, scale and movement should follow the size of what is moving.",
    issue: 17,
    slug: "4-principles-of-transition",
    title: "4 principles of transition",
  },
  {
    category: "general",
    date: month(2026, 9),
    description: "A look at the invisible side of design engineering.",
    issue: 18,
    slug: "the-invisible-side-of-design-engineering",
    title: "The invisible side of design engineering",
  },
  {
    category: "general",
    date: month(2026, 10),
    description:
      "An interface quiz that tests you on a variety of design engineering topics.",
    isNew: true,
    issue: 19,
    slug: "can-you-tell-the-difference",
    title: "Can you tell the difference?",
  },
  {
    category: "design",
    date: month(2026, 10),
    description: "Building a design system.",
    hidden: true,
    issue: 20,
    slug: "building-a-design-system",
    title: "Building a design system",
  },
] as const satisfies readonly ArticleMeta[];

export type ArticleSlug = (typeof RAW_ARTICLES)[number]["slug"];

export type FreeArticleSlug = Extract<
  (typeof RAW_ARTICLES)[number],
  { free: true }
>["slug"];

export interface ArticleMetaItem extends ArticleMeta {
  link: string;
  markdownLink: string;
  slug: ArticleSlug;
  url: string;
}

export const ARTICLE_META: ArticleMetaItem[] = RAW_ARTICLES.map((item) => {
  const link = `/magazine/issues/${item.slug}`;
  return {
    ...item,
    link,
    markdownLink: `/issues-md/${item.slug}`,
    url: `${domain}${link}`,
  };
});

export const ARTICLE_BY_SLUG = Object.fromEntries(
  ARTICLE_META.map((item) => [item.slug, item])
) as Record<ArticleSlug, ArticleMetaItem>;

const ARTICLE_SLUGS: ReadonlySet<string> = new Set(
  ARTICLE_META.map((item) => item.slug)
);

export const isArticleSlug = (slug: string): slug is ArticleSlug =>
  ARTICLE_SLUGS.has(slug);

export const VISIBLE_ARTICLE_META: ArticleMetaItem[] = ARTICLE_META.filter(
  (item) => !item.hidden
);

export function articleDate(date: Date): string {
  const monthPart = String(date.getMonth() + 1).padStart(2, "0");
  const dayPart = String(date.getDate()).padStart(2, "0");
  return `${date.getFullYear()}-${monthPart}-${dayPart}`;
}
